import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import {
  usePortalChat,
  usePortalConversationSummary,
} from "../../hooks/usePortalChat";
import { ALL_CLIENTS_KEY, useOrgStore } from "../../stores/org.store";
import { useOrganizations } from "../../hooks/use-organizations";
import type { ChatMessage } from "../../lib/chatApi";
import {
  postPortalCloseConversation,
  postPortalClosureResponse,
} from "../../lib/chatApi";
import { AvatarWithSkeleton, Button } from "@cliently/ui";
import { resolveOrganizationMediaUrl } from "../../lib/organization-media-url";

interface ConversationViewProps {
  conversationId: string;
}



export const ConversationView = ({ conversationId }: ConversationViewProps) => {
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const { data: organizations = [] } = useOrganizations();
  const queryClient = useQueryClient();
  const { data: summary } = usePortalConversationSummary(conversationId);
  const {
    messages,
    isLoading,
    sendMessage,
    toggleReaction,
    isTogglingReaction,
  } = usePortalChat(conversationId);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const currentClientIds =
    activeClientId === ALL_CLIENTS_KEY
      ? organizations.map((o) => o.clientId)
      : activeClientId
        ? [activeClientId]
        : [];

  const closureMutation = useMutation({
    mutationFn: (accepted: boolean) =>
      postPortalClosureResponse(conversationId, accepted),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["portal-conversation-summary", conversationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["portal-messages", conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
    },
  });

  const closeThread = useMutation({
    mutationFn: () => postPortalCloseConversation(conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["portal-conversation-summary", conversationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["portal-messages", conversationId],
      });
      void queryClient.invalidateQueries({ queryKey: ["portal-conversations"] });
    },
  });

  const buildReplyPayload = (content: string) => {
    if (!replyTo) return content;
    const sender = replyTo.senderLabel?.trim() || "message";
    const excerpt = replyTo.content.replace(/\s+/g, " ").trim().slice(0, 120);
    return `> Réponse à ${sender}: ${excerpt}\n\n${content}`;
  };

  const peerName = summary?.peerLabel?.trim() || "Conversation";
  const subjectLine = summary?.subject?.trim() || "";
  const avatarSrc = resolveOrganizationMediaUrl(
    summary?.org?.profilePhoto ?? summary?.org?.logo ?? undefined,
  );
  const initials = peerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const inputDisabled =
    summary?.status === "CLOSED" ||
    summary?.status === "CLOSURE_PENDING" ||
    closureMutation.isPending ||
    closeThread.isPending;

  return (
    <section className="bg-card flex h-full min-h-[420px] flex-col rounded-lg border">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <AvatarWithSkeleton
            className="h-11 w-11 shrink-0 border"
            src={avatarSrc ?? undefined}
            alt=""
            fallback={<span className="text-sm font-medium">{initials}</span>}
          />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold">{peerName}</h2>
            {summary?.org?.name ? (
              <p className="text-muted-foreground truncate text-xs">{summary.org.name}</p>
            ) : null}
            {subjectLine ? (
              <p className="text-muted-foreground mt-0.5 truncate text-xs">{subjectLine}</p>
            ) : null}
          </div>
        </div>
        {summary?.status === "OPEN" ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={closeThread.isPending}
            onClick={() => closeThread.mutate()}
          >
            Clôturer le fil
          </Button>
        ) : null}
      </header>

      {summary?.status === "CLOSED" ? (
        <div className="bg-muted/80 text-muted-foreground border-b px-4 py-2 text-sm">
          Cette conversation est clôturée.
        </div>
      ) : null}

      {summary?.status === "CLOSURE_PENDING" && summary.hasClientParticipant ? (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <p className="font-medium text-amber-950 dark:text-amber-100">
            Votre praticien souhaite clôturer cette discussion.
          </p>
          <p className="text-muted-foreground mt-1">
            Votre demande est-elle bien résolue ?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={closureMutation.isPending}
              onClick={() => closureMutation.mutate(true)}
            >
              Oui, clôturer
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={closureMutation.isPending}
              onClick={() => closureMutation.mutate(false)}
            >
              Non, continuer
            </Button>
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        {isLoading ? (
          <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
            Chargement des messages...
          </div>
        ) : (
          <MessageList
            messages={messages}
            currentClientIds={currentClientIds}
            onReply={setReplyTo}
            onToggleReaction={(message, emoji, reactedByMe) =>
              toggleReaction({ messageId: message.id, emoji, reactedByMe })
            }
            isTogglingReaction={isTogglingReaction}
          />
        )}
      </div>

      <MessageInput
        onSend={(content) => {
          sendMessage({ content: buildReplyPayload(content) });
          setReplyTo(null);
        }}
        replyPreview={replyTo}
        onCancelReply={() => setReplyTo(null)}
        disabled={inputDisabled}
      />
    </section>
  );
}
